import { MutableRefObject, useCallback, useEffect, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import {
    DraggableTab,
    DraggableTabList,
    TabPanel,
    Tabs,
    useTabsContext
} from "@/libraries/design/patterns/tabs/src";

import { Picture } from "../../../../../../components/wpl/image/src";
import {
    WCL_GRID_PADDING_SM,
    WCL_GRID_PADDING_LG,
    WCL_GRID_MAX_WIDTH
} from "../../../../../../libraries/design/foundations/grid/src/base";
import { Row } from "../../../../../../libraries/wpl/foundations/src";
import { ActiveTeamDataProps } from "../hero-jira-cta-template";

import { ActiveMedia } from "./active-media";
import { Team } from "./team-data";

const buttonResetStyle = css({
    "border": "none",
    "background": "transparent",
    "padding": "0",

    ":hover": {
        cursor: "pointer"
    }
});
const teamCardStyles = css({
    "boxSizing": "border-box",
    "display": "flex",
    "flexDirection": "column",
    "width": "173px",
    "height": "90px",
    "alignItems": "center",
    "padding": "8px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".hover-icon": {
        display: "none"
    },

    ":hover": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".default-icon": {
            display: "none"
        },

        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ".hover-icon": {
            display: "block"
        }
    }
});

const teamCardActiveStyles = css({
    border: `1px solid ${wacToken("color.primary.black")}`,
    borderRadius: "6px",

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    span: {
        fontWeight: "700"
    }
});
const iconContainerStyles = css({
    height: "48px",
    width: "48px"
});
const teamCardTextStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 400,
    fontSize: "1rem",
    lineHeight: "1.5rem",
    color: wacToken("color.primary.black")
});
const videoContainerStyles = css({
    "@keyframes fadein": {
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        from: {
            opacity: 0.7
        },
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        to: {
            opacity: 1
        }
    },

    "width": "100%",
    "aspectRatio": "16/9",
    "position": "relative",
    "marginBlockEnd": "8%"
});
const backgroundStyles = css({
    position: "absolute",
    insetInlineStart: "-100px",
    insetBlockStart: "58%",
    width: "calc(100% + 200px)",
    height: "55%",
    backgroundColor: wacToken("color.primary.blue"),
    zIndex: "-1",
    clipPath:
        "polygon(0.5% 0, 50% 0, 50% 10%, 98.5% 0%, 100% 85%, 17.75% 100%, 17.25% 85%, 0 85%)",

    [media.below.sm]: {
        insetInlineStart: "-50px"
    }
});

type TabButtonProps = {
    tabIndex: number;
    icon: string;
    iconHover: string;
    title: Record<string, string>;
    englishLabel: string;
    tabId: string;
    onMouseDown?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLButtonElement>) => void;
};

const TabButton = ({
    tabIndex,
    icon,
    iconHover,
    title,
    englishLabel,
    tabId,
    // these are passed down from DraggableTab
    onMouseDown,
    onClick,
    onKeyDown
}: TabButtonProps) => {
    const intl = useIntl();
    const { isCurrentTab } = useTabsContext();
    const isSelected = isCurrentTab(tabIndex);

    return (
        <button
            css={buttonResetStyle}
            data-product-key="home"
            data-event-component="button"
            data-event-container="heroSection"
            data-event="clicked"
            data-label-english={englishLabel}
            aria-label={intl.formatMessage(title)}
            role="tab"
            aria-selected={isSelected}
            aria-controls={`panel${tabId}`}
            onMouseDown={onMouseDown}
            onClick={onClick}
            onKeyDown={onKeyDown}
        >
            <div css={[teamCardStyles, isSelected && teamCardActiveStyles]}>
                <div css={[iconContainerStyles]} className="default-icon">
                    {
                        <Picture
                            image={{
                                src: `${icon}`,
                                alt: "",
                                draggable: "false",
                                width: 48,
                                height: 48
                            }}
                            forceEagerLoad={true}
                        />
                    }
                </div>
                <div css={[iconContainerStyles]} className="hover-icon">
                    <Picture
                        image={{
                            src: `${!isSelected ? iconHover : icon}`,
                            alt: "",
                            draggable: "false",
                            width: 48,
                            height: 48,
                            loading: "eager",
                            fetchpriority: "low"
                        }}
                    />
                </div>
                <span css={teamCardTextStyles}>
                    {intl.formatMessage(title)}
                </span>
            </div>
        </button>
    );
};

type TabPanelWrapperProps = {
    tab: Team;
    index: number;
};

const InitialContent = ({
    defaultTeamData
}: {
    defaultTeamData: ActiveTeamDataProps;
}) => {
    const { userSelected } = useTabsContext();

    // Show initial content only when no tab is selected
    if (userSelected) {
        return null;
    }

    return (
        <Row layout="10">
            <div css={videoContainerStyles} data-testid="default-content">
                <ActiveMedia {...defaultTeamData} />
                <div css={backgroundStyles} />
            </div>
        </Row>
    );
};

const ActiveTabPanel = ({ tab, index }: TabPanelWrapperProps) => {
    return (
        <TabPanel panelIndex={index} id={tab.id || `tab-${index}`}>
            <Row layout="10">
                <div css={videoContainerStyles}>
                    <ActiveMedia {...tab} />
                    <div css={backgroundStyles} />
                </div>
            </Row>
        </TabPanel>
    );
};

type ClickOutsideHandlerProps = {
    tabsWrapperRef: MutableRefObject<HTMLDivElement | null>;
};

const ClickOutsideHandler = ({ tabsWrapperRef }: ClickOutsideHandlerProps) => {
    const { goToTab, currentTab, unsetUserSelected } = useTabsContext();
    const currentTabRef = useRef(currentTab);

    // Keep ref in sync with currentTab for use in event listener
    useEffect(() => {
        currentTabRef.current = currentTab;
    }, [currentTab]);

    // Reset tab selection when user clicks outside the tabs container
    const handleClickOutside = useCallback(
        (event: MouseEvent) => {
            if (
                currentTabRef.current >= 0 &&
                tabsWrapperRef.current &&
                !tabsWrapperRef.current.contains(event.target as Node)
            ) {
                goToTab(-1);
                unsetUserSelected();
            }
        },
        [goToTab, tabsWrapperRef, unsetUserSelected]
    );

    useEffect(() => {
        // Attach the listener on component mount
        document.addEventListener("mousedown", handleClickOutside);

        // Detach the listener on component unmount
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [handleClickOutside]);

    return null;
};

const fullWidthContainerStyles = css({
    margin: "auto",
    maxWidth: "100%",
    boxSizing: "border-box",
    overflow: "visible",
    paddingInlineStart: `${WCL_GRID_PADDING_SM}px`,
    paddingInlineEnd: `${WCL_GRID_PADDING_SM}px`,
    width: "100%",
    [media.only.sm]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`
    },
    [media.above.md]: {
        paddingInlineStart: `${WCL_GRID_PADDING_LG}px`,
        paddingInlineEnd: `${WCL_GRID_PADDING_LG}px`,
        maxWidth: `${WCL_GRID_MAX_WIDTH + WCL_GRID_PADDING_LG * 2}px`
    }
});

export const HeroTabs = ({
    teamData,
    defaultTeamData
}: {
    teamData: Team[];
    defaultTeamData: ActiveTeamDataProps;
}) => {
    const tabsWrapperRef: MutableRefObject<HTMLDivElement | null> =
        useRef(null);
    const intl = useIntl();

    return (
        <>
            <div ref={tabsWrapperRef}>
                <Tabs initialTab={-1}>
                    <ClickOutsideHandler tabsWrapperRef={tabsWrapperRef} />
                    <DraggableTabList tabSize="large" alignment="center">
                        {teamData.map(
                            (
                                { title, icon, englishLabel, iconHover, id },
                                index
                            ) => (
                                <DraggableTab
                                    key={index}
                                    tabIndex={index}
                                    title={intl.formatMessage(title)}
                                    iconUrl={icon}
                                    tabSize="large"
                                    bgColor="bgWhite"
                                    className="round-tab"
                                    enableRovoBorder={false}
                                    enableResponsiveTabSize={false}
                                >
                                    <TabButton
                                        tabIndex={index}
                                        icon={icon}
                                        iconHover={iconHover}
                                        title={title}
                                        englishLabel={englishLabel}
                                        tabId={id}
                                    />
                                </DraggableTab>
                            )
                        )}
                    </DraggableTabList>
                    <div css={fullWidthContainerStyles} className="tab-panels">
                        <InitialContent defaultTeamData={defaultTeamData} />
                        {teamData.map((tab, index) => (
                            <ActiveTabPanel
                                key={index}
                                tab={tab}
                                index={index}
                            />
                        ))}
                    </div>
                </Tabs>
            </div>
        </>
    );
};
